import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Clock3 } from "lucide-react";
import "../radar/radar.css";
import { RadarAlertButton, RadarAlertDialog, RadarAlertPanel } from "./RadarAlertPanel";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { RadarMap } from "../radar/RadarMap";
import { RadarLegend, RadarTimeline, RadarToolbar, scanLabel } from "../radar/RadarControls";
import { DEFAULT_VIEW, PREFS_KEY, VIEW_KEY, loadJSON, saveJSON } from "../radar/radarConfig";
import {
  productById, useActiveSite, useFreshnessBucket, useGeolocate, useNWSAlerts, useRadarLoop, useRadarScans, useRadarSites, useStormReports,
} from "../radar/useRadarData";

const BANNER_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_desk-custom/artifacts/dleu4wtz_IMG_6894.webp";
const DEFAULT_PREFS = { product: "composite", satellite: "none", basemap: "dark", overlays: { warnings: true, labels: true, sites: false, reports: false }, radarOpacity: 0.85 };

// One canonical Storm Desk radar (NEXRAD + GOES satellite + NWS warnings). Every surface renders this map;
// `compact` is used by studio monitors (latest frame only, no operator controls).
export const StormDeskRadar = ({ height = 620, hideHeader = false, hidePanel = false, initialPanelCollapsed = false, relayRunning = false, compact = false }) => {
  const rootRef = useRef(null);
  const mapRef = useRef(null);
  const [prefs, setPrefs] = useState(() => (compact ? DEFAULT_PREFS : loadJSON(PREFS_KEY, DEFAULT_PREFS)));
  const [view, setView] = useState(() => (compact ? DEFAULT_VIEW : loadJSON(VIEW_KEY, DEFAULT_VIEW)));
  const [siteOverride, setSiteOverride] = useState(null);
  const [playing, setPlaying] = useState(!compact);
  const [filter, setFilter] = useState("All");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [panelCollapsed, setPanelCollapsed] = useState(initialPanelCollapsed);
  const [mobileAlertsOpen, setMobileAlertsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [now, setNow] = useState(new Date());

  const product = productById(prefs.product);
  const frameCount = compact ? 1 : product.frames;
  const sites = useRadarSites();
  const site = useActiveSite(sites, view, siteOverride);
  const scans = useRadarScans(product.id, site?.id, !compact);
  const reports = useStormReports(prefs.overlays.reports && !compact);
  const bucket = useFreshnessBucket();
  const { alerts, loading, lastUpdated } = useNWSAlerts();
  const [frame, setFrame] = useRadarLoop(frameCount, playing && !compact);
  const { locate, locating, location } = useGeolocate(mapRef, () => setSiteOverride(null));

  useEffect(() => { if (!compact) saveJSON(PREFS_KEY, prefs); }, [prefs, compact]);
  useEffect(() => { if (!compact) saveJSON(VIEW_KEY, view); }, [view, compact]);
  useEffect(() => { setPanelCollapsed(initialPanelCollapsed); }, [initialPanelCollapsed]);
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    const onFull = () => setIsFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", onFull);
    return () => { clearInterval(timer); document.removeEventListener("fullscreenchange", onFull); };
  }, []);

  const updatePref = useCallback((key, value) => setPrefs((current) => ({ ...current, [key]: typeof value === "function" ? value(current[key]) : value })), []);
  const setProduct = useCallback((id) => {
    updatePref("product", id);
    const next = productById(id);
    if (next.site && mapRef.current && mapRef.current.getZoom() < 6 && site) mapRef.current.flyTo([site.lat, site.lon], 7, { duration: 0.6 });
  }, [updatePref, site]);
  const selectSite = useCallback((chosen) => {
    setSiteOverride(chosen.id);
    if (!product.site) updatePref("product", "N0B");
    mapRef.current?.flyTo([chosen.lat, chosen.lon], Math.max(mapRef.current.getZoom(), 7), { duration: 0.6 });
  }, [product.site, updatePref]);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  const filtered = useMemo(() => (filter === "All" ? alerts : alerts.filter((alert) => (alert.event || "").toLowerCase().includes(filter.toLowerCase()))), [alerts, filter]);
  const counts = useMemo(() => alerts.reduce((result, alert) => {
    const event = (alert.event || "").toLowerCase();
    if (event.includes("tornado warning")) result.tor += 1;
    else if (event.includes("severe thunderstorm warning")) result.svr += 1;
    else if (event.includes("flash flood") || event.includes("flood warning")) result.ff += 1;
    return result;
  }, { tor: 0, svr: 0, ff: 0 }), [alerts]);

  const scanTime = scanLabel(scans, frame, frameCount, product.site ? 6 : 5);
  const sourceLabel = product.site ? `${site?.tile || "—"} ${site?.name || ""}`.trim() : "US Composite";

  return (
    <div ref={rootRef} data-testid="storm-desk-radar" className="sd-radar-root relative flex h-full flex-col overflow-hidden border border-white/10 bg-[#02040c]">
      {!hideHeader && !isFullscreen && (
        <div className="relative h-16 shrink-0 overflow-hidden sm:h-20" data-testid="storm-desk-radar-branding">
          <img src={BANNER_IMG} alt="Storm Desk Live Weather" className="absolute inset-0 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-between px-3 sm:flex">
            <span className="mono bg-black/55 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Storm Desk Live Weather</span>
            <span className="mono inline-flex items-center gap-1.5 border border-blue-300/25 bg-[#071126]/90 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> NEXRAD · GOES · NWS
            </span>
          </div>
        </div>
      )}

      {!hidePanel && !isFullscreen && (
        <>
          <div className="relative shrink-0 border-b border-white/10 bg-[#050915] p-2 md:hidden">
            <button type="button" data-testid="radar-mobile-alerts-button" onClick={() => setMobileAlertsOpen(true)} className="flex w-full items-center justify-between border border-white/10 bg-black/40 px-3 py-2 text-left text-sm font-semibold text-slate-100 focus:border-cyan-400 focus:outline-none">
              <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /> {loading ? "Loading NWS alerts…" : "Active NWS alerts"}</span>
              <span className="mono border border-red-500/30 bg-red-500/15 px-2 py-0.5 text-[10px] text-red-200">{alerts.length}</span>
            </button>
          </div>
          <Sheet open={mobileAlertsOpen} onOpenChange={setMobileAlertsOpen}>
            <SheetContent side="left" data-testid="radar-mobile-alerts-sheet" className="w-[92vw] border-white/10 bg-[#070b15] p-0 text-slate-100 sm:max-w-md">
              <SheetHeader className="border-b border-white/10 p-4 text-left">
                <SheetTitle className="display text-xl font-black uppercase text-white">Active NWS Alerts</SheetTitle>
                <SheetDescription className="text-xs text-slate-400">Live National Weather Service products. Select one for details.</SheetDescription>
              </SheetHeader>
              <div className="max-h-[calc(100vh-90px)] divide-y divide-white/5 overflow-y-auto">
                {!loading && filtered.length === 0 && <div className="p-6 text-center text-sm text-emerald-300">No active alerts.</div>}
                {filtered.map((alert, index) => (
                  <RadarAlertButton key={`${alert.id || alert.event}-${index}`} alert={alert} index={index} testIdPrefix="radar-mobile-alert-item" onSelect={(item) => { setMobileAlertsOpen(false); setSelectedAlert(item); }} />
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}

      <div className="relative flex min-h-0 shrink-0" style={{ height: isFullscreen ? "100vh" : height }}>
        {!hidePanel && !isFullscreen && (
          <RadarAlertPanel
            alerts={alerts} filtered={filtered} filter={filter} setFilter={setFilter} loading={loading} lastUpdated={lastUpdated} counts={counts}
            selectedAlert={selectedAlert} setSelectedAlert={setSelectedAlert} collapsed={panelCollapsed} onToggle={() => setPanelCollapsed((value) => !value)}
            radarReady={sites.length > 0} relayRunning={relayRunning}
          />
        )}
        {(hidePanel || isFullscreen) && <RadarAlertDialog selectedAlert={selectedAlert} setSelectedAlert={setSelectedAlert} />}

        <div className="relative isolate min-w-0 flex-1 overflow-hidden bg-[#02040c]" data-testid="storm-desk-radar-map">
          <RadarMap
            view={view} onViewChange={setView} mapRef={mapRef} product={product} site={site} frame={frame} frameCount={frameCount}
            radarOpacity={prefs.radarOpacity} bucket={bucket} satellite={prefs.satellite} overlays={prefs.overlays} alerts={alerts} sites={sites}
            reports={reports} location={location} onSelectAlert={setSelectedAlert} onSelectSite={selectSite} compact={compact} basemap={prefs.basemap}
          />

          <div data-testid="radar-live-status-dock" className="pointer-events-none absolute left-2 top-2 z-[420] flex max-w-[calc(100%-16px)] flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border border-cyan-300/25 bg-[rgba(2,6,16,0.85)] px-2.5 py-1.5 shadow-lg backdrop-blur-md">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
              <span className="mono text-[10px] font-bold uppercase tracking-[0.18em] text-white">Storm Desk Radar</span>
              <span data-testid="radar-live-clock" className="mono flex shrink-0 items-center gap-1 border-l border-white/15 pl-2 text-[10px] font-bold text-cyan-100" aria-label="Current local time">
                <Clock3 className="h-3 w-3 text-cyan-300" />
                {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            </div>
            {!compact && (
              <div data-testid="radar-product-chip" className="mono hidden items-center gap-2 border border-white/15 bg-[rgba(2,6,16,0.85)] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-200 backdrop-blur-md sm:flex">
                <span className="text-cyan-300">{product.short}</span>
                <span className="border-l border-white/15 pl-2">{sourceLabel}</span>
                <span className="border-l border-white/15 pl-2 text-slate-400">{scanTime}</span>
              </div>
            )}
          </div>

          {!compact && (
            <>
              <RadarToolbar
                product={product} setProduct={setProduct} satellite={prefs.satellite} setSatellite={(value) => updatePref("satellite", value)}
                overlays={prefs.overlays} setOverlays={(value) => updatePref("overlays", value)} radarOpacity={prefs.radarOpacity} setRadarOpacity={(value) => updatePref("radarOpacity", value)}
                sites={sites} site={site} siteOverride={siteOverride} setSiteOverride={setSiteOverride} locate={locate} locating={locating}
                toggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} mapRef={mapRef} basemap={prefs.basemap} setBasemap={(value) => updatePref("basemap", value)}
              />
              <RadarTimeline frame={frame} setFrame={setFrame} frameCount={frameCount} playing={playing} setPlaying={setPlaying} scanTime={scanTime} />
              <RadarLegend product={product} />
            </>
          )}
          <div className="pointer-events-none absolute bottom-0 right-12 z-[410] hidden px-1 text-[8px] text-slate-500 sm:block">© CARTO · NOAA NEXRAD/GOES via IEM · NASA GIBS · NWS</div>
        </div>
      </div>
    </div>
  );
};

export default StormDeskRadar;
